import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  FrontClearanceDeletePayload,
  FrontClearanceFilterEnum,
  FrontClearanceFilterOption,
  FrontClearanceFilterValue,
  FrontClearanceForm,
  FrontClearanceFormDropDownKey,
  FrontClearanceListMeta,
  FrontClearanceRow,
  FrontClearanceUpdatePayload,
} from "../types";
import {
  initialFormDropDownState,
  initialFrontClearanceForm,
  initialState,
} from "./initialState";

export const sliceKey = "frontClearanceState";

type FrontClearanceListSuccessPayload = {
  data: FrontClearanceRow[];
  meta: FrontClearanceListMeta;
};

type FrontClearanceFilterOptionsSuccessPayload = {
  type: FrontClearanceFilterEnum;
  data: FrontClearanceFilterOption[];
};

type FormDropDownSuccessPayload = {
  key: FrontClearanceFormDropDownKey;
  data: FrontClearanceFilterOption[];
  meta: FrontClearanceListMeta;
};

const frontClearanceSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getFrontClearanceList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getFrontClearanceListSuccess(
      state,
      action: PayloadAction<FrontClearanceListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getFrontClearanceListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<FrontClearanceListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setManufacturerFilter(
      state,
      action: PayloadAction<FrontClearanceFilterValue>,
    ) {
      state.manufacturerFilter = action.payload;
    },

    setMasterModelFilter(
      state,
      action: PayloadAction<FrontClearanceFilterValue>,
    ) {
      state.masterModelFilter = action.payload;
    },

    setYearFilter(state, action: PayloadAction<FrontClearanceFilterValue>) {
      state.yearFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialFrontClearanceForm;
      state.formDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        superModels: { ...initialFormDropDownState },
      };
    },

    getFormDropDownList(
      state,
      action: PayloadAction<FrontClearanceFormDropDownKey>,
    ) {
      const key = action.payload;
      state.formDropDowns[key] = {
        ...initialFormDropDownState,
        perPage: state.formDropDowns[key].perPage,
        loading: true,
      };
    },

    getFormDropDownListSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      const { key, data, meta } = action.payload;
      state.formDropDowns[key] = {
        ...state.formDropDowns[key],
        items: data,
        meta,
        page: meta.currentPage,
        loading: false,
      };
    },

    getFormDropDownListError(
      state,
      action: PayloadAction<{
        key: FrontClearanceFormDropDownKey;
        message: string;
      }>,
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    getFormDropDownNextPage(
      state,
      action: PayloadAction<FrontClearanceFormDropDownKey>,
    ) {
      const dropDown = state.formDropDowns[action.payload];
      if (
        dropDown.loading ||
        dropDown.meta.currentPage >= dropDown.meta.totalPages
      ) {
        return;
      }
      dropDown.loading = true;
      dropDown.page = dropDown.meta.currentPage + 1;
    },

    getFormDropDownNextPageSuccess(
      state,
      action: PayloadAction<FormDropDownSuccessPayload>,
    ) {
      const { key, data, meta } = action.payload;
      const dropDown = state.formDropDowns[key];
      dropDown.loading = false;
      dropDown.items = dropDown.items.concat(data);
      dropDown.meta = meta;
      dropDown.page = meta.currentPage;
    },

    getFormDropDownNextPageError(
      state,
      action: PayloadAction<{
        key: FrontClearanceFormDropDownKey;
        message: string;
      }>,
    ) {
      state.formDropDowns[action.payload.key].loading = false;
      state.error = action.payload.message;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof FrontClearanceForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialFrontClearanceForm;
    },

    addFrontClearance(state) {
      state.saving = true;
      state.error = null;
    },

    addFrontClearanceSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialFrontClearanceForm;
      state.formDropDowns = {
        manufacturers: { ...initialFormDropDownState },
        superModels: { ...initialFormDropDownState },
      };
      state.error = null;
    },

    addFrontClearanceError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    updateFrontClearance(
      state,
      _action: PayloadAction<FrontClearanceUpdatePayload>,
    ) {
      state.saving = true;
      state.error = null;
    },

    updateFrontClearanceSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    updateFrontClearanceError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    deleteFrontClearance(
      state,
      _action: PayloadAction<FrontClearanceDeletePayload>,
    ) {
      state.saving = true;
      state.error = null;
    },

    deleteFrontClearanceSuccess(state) {
      state.saving = false;
      state.error = null;
    },

    deleteFrontClearanceError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },

    getFilterOptions(
      state,
      _action: PayloadAction<FrontClearanceFilterEnum>,
    ) {
      state.filterOptionsLoading = true;
      state.filterOptionsError = null;
    },

    getFilterOptionsSuccess(
      state,
      action: PayloadAction<FrontClearanceFilterOptionsSuccessPayload>,
    ) {
      const { type, data } = action.payload;
      state.filterOptions[type] = Array.isArray(data) ? data : [];
      state.filterOptionsLoading = false;
      state.filterOptionsError = null;
    },

    getFilterOptionsError(state, action: PayloadAction<string>) {
      state.filterOptionsLoading = false;
      state.filterOptionsError = action.payload;
    },
  },
});

export const { actions: frontClearanceActions, reducer } = frontClearanceSlice;
